import type { ApiError, ApiRoutes } from '@gd/shared'

/** Thrown by `api.get`/`post`/`put`/`delete` for any non-2xx response or network failure. */
export class ApiClientError extends Error {
  readonly code: string
  readonly status: number

  constructor(message: string, code: string, status: number) {
    super(message)
    this.name = 'ApiClientError'
    this.code = code
    this.status = status
  }
}

type Route = keyof ApiRoutes
type RouteDef<K extends Route> = ApiRoutes[K]
type RouteQuery<K extends Route> = RouteDef<K> extends { query: infer Q } ? Q : undefined
type RouteBody<K extends Route> = RouteDef<K> extends { body: infer B } ? B : undefined
type RouteResponse<K extends Route> = RouteDef<K>['response']

/** Substitute `:param` segments in a route with real values, e.g. "/api/athletes/:id". */
function resolvePath(path: string, params: Record<string, string | number> | undefined): string {
  if (!params) return path
  return path.replace(/:([a-zA-Z]+)/g, (match, key: string) => {
    const value = params[key]
    return value === undefined ? match : encodeURIComponent(String(value))
  })
}

function buildQueryString(query: Record<string, unknown> | undefined): string {
  if (!query) return ''
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue
    params.set(key, String(value))
  }
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

async function request<T>(input: RequestInfo, init?: RequestInit): Promise<T> {
  let res: Response
  try {
    res = await fetch(input, init)
  } catch {
    throw new ApiClientError('Network request failed', 'network_error', 0)
  }

  if (!res.ok) {
    let parsed: ApiError | null = null
    try {
      parsed = (await res.json()) as ApiError
    } catch {
      parsed = null
    }
    const code = parsed?.error?.code ?? `http_${res.status}`
    const message = parsed?.error?.message ?? (res.statusText || 'Request failed')
    throw new ApiClientError(message, code, res.status)
  }

  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

export interface GetOptions<K extends Route> {
  params?: Record<string, string | number>
  query?: RouteQuery<K>
  signal?: AbortSignal
}

export interface PostOptions<K extends Route> {
  params?: Record<string, string | number>
  body?: RouteBody<K>
  signal?: AbortSignal
}

/**
 * Typed fetcher over the API contract in `@gd/shared`. `route` is the "METHOD /api/path"
 * key from `ApiRoutes`; `:param` segments are filled from `options.params`.
 */
export const api = {
  async get<K extends Route & `GET ${string}`>(
    route: K,
    options: GetOptions<K> = {},
  ): Promise<RouteResponse<K>> {
    const path = route.slice('GET '.length)
    const url =
      resolvePath(path, options.params) + buildQueryString(options.query as Record<string, unknown>)
    return request<RouteResponse<K>>(url, { method: 'GET', signal: options.signal })
  },

  async post<K extends Route & `POST ${string}`>(
    route: K,
    options: PostOptions<K> = {},
  ): Promise<RouteResponse<K>> {
    const path = route.slice('POST '.length)
    const url = resolvePath(path, options.params)
    return request<RouteResponse<K>>(url, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    })
  },

  async put<K extends Route & `PUT ${string}`>(
    route: K,
    options: PostOptions<K> = {},
  ): Promise<RouteResponse<K>> {
    const path = route.slice('PUT '.length)
    const url = resolvePath(path, options.params)
    return request<RouteResponse<K>>(url, {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    })
  },

  async delete<K extends Route & `DELETE ${string}`>(
    route: K,
    options: Omit<PostOptions<K>, 'body'> = {},
  ): Promise<RouteResponse<K>> {
    const path = route.slice('DELETE '.length)
    const url = resolvePath(path, options.params)
    return request<RouteResponse<K>>(url, { method: 'DELETE', signal: options.signal })
  },
}
